import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  Calendar, Users, FileBarChart, Settings, LogOut, Shield, TrendingUp, HeartPulse,
  ShieldCheck, UserCog, Search, ClipboardList, Inbox, DollarSign, Activity, ClipboardCheck, Phone, FlaskConical,
  Bug, LayoutDashboard, MessageSquare, ChevronDown
} from 'lucide-react';

// Grouped nav structure — reduces visual overload
const NAV_GROUPS = [
  {
    id: 'clinical',
    label: 'Clinical',
    items: [
      { path: '/my-day', label: 'My Day', icon: LayoutDashboard, roles: ['provider', 'admin'] },
      { path: '/nurse-messages', label: 'Messages', icon: MessageSquare, roles: ['nurse'] },
      { path: '/schedule', label: 'Schedule', icon: Calendar, roles: ['front_desk', 'provider', 'nurse', 'office_manager', 'admin', 'billing', 'coder', 'work_clerk'] },
      { path: '/patients', label: 'Patients', icon: Search, roles: ['provider', 'nurse', 'front_desk', 'office_manager', 'admin', 'billing', 'coder', 'work_clerk'] },
      { path: '/queue', label: 'Provider Queue', icon: Users, roles: ['provider', 'nurse', 'office_manager', 'admin'] },
      { path: '/pre-visit', label: 'Pre-Visit Prep', icon: ClipboardList, roles: ['provider', 'nurse', 'office_manager', 'admin'] },
    ],
  },
  {
    id: 'documents',
    label: 'Documents',
    items: [
      { path: '/documents', label: 'My Documents', icon: Inbox, roles: ['provider', 'admin'] },
      { path: '/doc-manager', label: 'Doc Manager', icon: FileBarChart, roles: ['admin', 'office_manager', 'front_desk'] },
    ],
  },
  {
    id: 'revenue',
    label: 'Revenue & Coding',
    collapsed: true, // default collapsed for non-billing roles
    items: [
      { path: '/revenue', label: 'Revenue Cycle', icon: DollarSign, roles: ['billing', 'coder', 'office_manager', 'admin', 'provider'] },
      { path: '/rcm-analytics', label: 'Revenue Analytics', icon: TrendingUp, roles: ['billing', 'coder', 'office_manager', 'admin'] },
      { path: '/rcm-workspace', label: 'RCM Workspace', icon: ClipboardCheck, roles: ['billing', 'office_manager', 'admin'] },
      { path: '/coding-workspace', label: 'Coding Workspace', icon: ClipboardList, roles: ['coder', 'billing', 'office_manager', 'admin'] },
      { path: '/billing/test-report', label: 'Billing Test Report', icon: FileBarChart, roles: ['provider', 'coder', 'billing', 'office_manager', 'admin'] },
      { path: '/comparisons', label: 'Comparisons', icon: FileBarChart, roles: ['billing', 'coder', 'admin'] },
      { path: '/work-clerk', label: 'Work Clerk', icon: ClipboardList, roles: ['work_clerk', 'office_manager', 'admin'] },
    ],
  },
  {
    id: 'quality',
    label: 'Quality',
    collapsed: true,
    items: [
      { path: '/quality-dashboard', label: 'Quality Dashboard', icon: Activity, roles: ['provider', 'office_manager', 'admin', 'coder'] },
      { path: '/quality-outreach', label: 'Outreach Queue', icon: Phone, roles: ['front_desk', 'nurse', 'office_manager', 'admin', 'coder'] },
      { path: '/quality-registry', label: 'Quality Registry', icon: ClipboardCheck, roles: ['provider', 'office_manager', 'admin', 'coder'] },
    ],
  },
];

const SUPER_USER_ITEMS = [
  { path: '/qil-test-dashboard', label: 'QIL Testing', icon: FlaskConical },
  { path: '/settings', label: 'Settings', icon: Settings },
  { path: '/metrics', label: 'Visit Metrics', icon: TrendingUp },
  { path: '/audit', label: 'Audit Trail', icon: Shield },
  { path: '/users', label: 'User Management', icon: UserCog },
];

// 2 states: 'full' (text+icons), 'icons' (icons only)
const MODES = ['icons', 'full'];

export default function NavRail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mode, setMode] = useState(() => localStorage.getItem('hfc_nav_mode') || 'icons');
  const [nurseBadge, setNurseBadge] = useState(0);
  const [devRole, setDevRole] = useState(() => localStorage.getItem('hfc_dev_role') || '');

  // Apply dev role header to all API calls
  useEffect(() => {
    if (devRole) {
      api.defaults.headers.common['X-Dev-Role'] = devRole;
      localStorage.setItem('hfc_dev_role', devRole);
    } else {
      delete api.defaults.headers.common['X-Dev-Role'];
      localStorage.removeItem('hfc_dev_role');
    }
  }, [devRole]);

  const clearDevRole = () => { setDevRole(''); };

  const DEV_ROLES = ['admin', 'billing', 'office_manager', 'coder', 'provider', 'nurse', 'front_desk'];
  const effectiveRole = devRole || user?.role;

  // Poll nurse badge count for nurse/admin roles
  useEffect(() => {
    if (!user || !['nurse', 'admin', 'office_manager'].includes(user.role)) return;
    const fetchBadge = () => {
      api.get('/queues/nurse-badge').then(res => {
        setNurseBadge(res.data.pending_tasks || 0);
      }).catch(() => {});
    };
    fetchBadge();
    const interval = setInterval(fetchBadge, 30000); // every 30s
    return () => clearInterval(interval);
  }, [user]);

  const cycleMode = () => {
    const nextIdx = (MODES.indexOf(mode) + 1) % MODES.length;
    const next = MODES[nextIdx];
    setMode(next);
    localStorage.setItem('hfc_nav_mode', next);
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const isSuperUser = user?.is_super_user;

  // Track collapsed groups
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    const saved = localStorage.getItem('hfc_nav_collapsed');
    if (saved) return JSON.parse(saved);
    // Default: collapse revenue + quality for non-billing roles
    const defaults = {};
    NAV_GROUPS.forEach(g => { if (g.collapsed) defaults[g.id] = true; });
    return defaults;
  });

  const toggleGroup = (groupId) => {
    setCollapsedGroups(prev => {
      const next = { ...prev, [groupId]: !prev[groupId] };
      localStorage.setItem('hfc_nav_collapsed', JSON.stringify(next));
      return next;
    });
  };

  const isFull = mode === 'full';
  const width = isFull ? 'w-52' : 'w-14';

  return (
    <TooltipProvider delayDuration={200}>
      <nav className={`flex flex-col h-[calc(100%-12px)] ${width} bg-white shrink-0 transition-all duration-200 rounded-xl shadow-[0_2px_12px_rgba(0,0,0,0.07)] border border-slate-200/70 m-1.5 ml-1.5`} data-testid="nav-rail">
        {/* Logo + Toggle */}
        <div className={`flex items-center ${isFull ? 'gap-2 px-3' : 'justify-center px-1'} py-3 shrink-0`}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary text-primary-foreground hover:opacity-90 active:scale-95 transition-all shrink-0"
                onClick={cycleMode}
                data-testid="nav-toggle"
              >
                <HeartPulse className="w-4.5 h-4.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {isFull ? 'Collapse menu' : 'Expand menu'}
            </TooltipContent>
          </Tooltip>
          {isFull && (
            <div className="min-w-0">
              <h1 className="text-sm font-semibold leading-tight" style={{ fontFamily: 'Space Grotesk' }}>CFC</h1>
              <p className="text-[9px] text-muted-foreground">ChartFlow Clinic</p>
            </div>
          )}
        </div>

        {/* Scrollable Nav Items */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-1.5 pt-0 space-y-0.5">
          {NAV_GROUPS.map(group => {
            const visibleItems = group.items.filter(item => !item.roles || item.roles.includes(effectiveRole) || isSuperUser);
            if (visibleItems.length === 0) return null;
            const isCollapsed = collapsedGroups[group.id];

            return (
              <div key={group.id}>
                {/* Group header — clickable to collapse */}
                {isFull && NAV_GROUPS.indexOf(group) > 0 && (
                  <button onClick={() => toggleGroup(group.id)}
                    className="flex items-center gap-1 px-2.5 py-1 mt-1.5 w-full text-left"
                    data-testid={`nav-group-${group.id}`}>
                    <ChevronDown className={`w-3 h-3 text-slate-300 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                    <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">{group.label}</span>
                  </button>
                )}
                {!isFull && NAV_GROUPS.indexOf(group) > 0 && (
                  <div className="border-t border-slate-100 my-1 mx-2" />
                )}

                {/* Group items */}
                {(!isCollapsed || !isFull) && visibleItems.map(item => {
                  const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                  return (
                    <Tooltip key={item.path}>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          className={`${isFull ? 'justify-start gap-2.5 px-2.5' : 'justify-center px-0'} h-8 w-full transition-colors
                            ${isActive
                              ? 'bg-primary/[0.08] text-primary font-medium border-l-2 border-primary rounded-l-none'
                              : 'hover:bg-accent text-slate-600'}`}
                          onClick={() => navigate(item.path)}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <div className="relative">
                            <item.icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-primary' : ''}`} />
                            {item.path === '/queue' && nurseBadge > 0 && (
                              <span className="absolute -top-1 -right-1.5 w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[7px] font-bold flex items-center justify-center">{nurseBadge > 9 ? '9+' : nurseBadge}</span>
                            )}
                          </div>
                          {isFull && <span className="text-[12px] truncate">{item.label}</span>}
                        </Button>
                      </TooltipTrigger>
                      {!isFull && <TooltipContent side="right">{item.label}</TooltipContent>}
                    </Tooltip>
                  );
                })}
              </div>
            );
          })}

          {/* Super User Section */}
          {isSuperUser && (
            <>
              {isFull ? (
                <button onClick={() => toggleGroup('super')}
                  className="flex items-center gap-1 px-2.5 py-1 mt-2 w-full text-left">
                  <ChevronDown className={`w-3 h-3 text-amber-400 transition-transform ${collapsedGroups.super ? '-rotate-90' : ''}`} />
                  <ShieldCheck className="w-3 h-3 text-amber-500" />
                  <span className="text-[9px] font-semibold uppercase tracking-wider text-amber-600">Super User</span>
                </button>
              ) : (
                <div className="flex justify-center mt-1">
                  <ShieldCheck className="w-3 h-3 text-amber-500" />
                </div>
              )}
              {(!collapsedGroups.super || !isFull) && SUPER_USER_ITEMS.map(item => {
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        className={`${isFull ? 'justify-start gap-2.5 px-2.5' : 'justify-center px-0'} h-7 w-full transition-colors
                          ${isActive
                            ? 'bg-amber-50 text-amber-700 font-medium border-l-2 border-amber-500 rounded-l-none'
                            : 'hover:bg-accent text-muted-foreground'}`}
                        onClick={() => navigate(item.path)}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <item.icon className="w-3.5 h-3.5 shrink-0" />
                        {isFull && <span className="text-[11px] truncate">{item.label}</span>}
                      </Button>
                    </TooltipTrigger>
                    {!isFull && <TooltipContent side="right">{item.label}</TooltipContent>}
                  </Tooltip>
                );
              })}
            </>
          )}
        </div>

        {/* Dev Role Switcher — only in non-production */}
        {isFull && (
          <div className="border-t p-1.5">
            {devRole && (
              <div className="bg-orange-100 border border-orange-300 rounded px-2 py-1 mb-1.5 text-[9px] text-orange-800 font-medium text-center" data-testid="dev-role-banner">
                DEV MODE — ROLE OVERRIDE ACTIVE
              </div>
            )}
            <div className="px-1.5 mb-1">
              <select
                value={devRole}
                onChange={e => setDevRole(e.target.value)}
                className="w-full text-[10px] border rounded px-1.5 py-1 bg-card"
                data-testid="dev-role-select"
              >
                <option value="">Dev Role: Off</option>
                {DEV_ROLES.map(r => (
                  <option key={r} value={r}>{r === effectiveRole && devRole ? `✓ ${r}` : r}</option>
                ))}
              </select>
            </div>
            {devRole && (
              <Button variant="ghost" size="sm" className="w-full h-6 text-[10px] text-orange-600" onClick={clearDevRole} data-testid="clear-dev-role">
                Clear Override
              </Button>
            )}
          </div>
        )}
        {!isFull && devRole && (
          <div className="border-t p-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-10 h-5 mx-auto bg-orange-100 border border-orange-300 rounded flex items-center justify-center cursor-pointer" onClick={clearDevRole}>
                  <Bug className="w-3 h-3 text-orange-600" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="right">Dev Role: {devRole} (click to clear)</TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* User / Logout */}
        <div className="border-t p-1.5">
          {isFull && (
            <div className="flex items-center gap-2 px-2.5 py-1.5 mb-1">
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-medium text-primary">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{user?.name}</p>
                <p className="text-[9px] text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                className={`w-full ${isFull ? 'justify-start gap-2.5 px-2.5' : 'justify-center px-0'} h-8 text-muted-foreground`}
                onClick={handleLogout}
                data-testid="nav-logout"
              >
                <LogOut className="w-4 h-4" />
                {isFull && <span className="text-sm">Sign Out</span>}
              </Button>
            </TooltipTrigger>
            {!isFull && <TooltipContent side="right">Sign Out</TooltipContent>}
          </Tooltip>

          {/* Version display */}
          {process.env.REACT_APP_VERSION && (
            <div className="px-2.5 pb-1">
              <p className="text-[9px] text-muted-foreground/50 font-mono text-center select-none"
                 data-testid="app-version"
                 title={`Deployed: ${process.env.REACT_APP_VERSION}`}>
                {isFull ? `v${process.env.REACT_APP_VERSION}` : process.env.REACT_APP_VERSION.split('.').slice(0,2).join('.')}
              </p>
            </div>
          )}
        </div>
      </nav>
    </TooltipProvider>
  );
}
